import { useEffect, useState } from 'react';

import { waitForElement } from '../../utils/waitForElement';

/**
 * Use this hook to get a portal container for a given selector.
 * It will rerender the component when the container is added or removed from the DOM.
 * @param selector - The selector to use to find the container element.
 * @returns The container element or null if it is not found.
 */
export function usePortalContainer(selector: string): HTMLElement | null {
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!targetElement) {
      // Wait for targetElement to appear in the DOM
      let cancelled = false;
      const { elementPromise, observer } = waitForElement(selector);
      elementPromise.then((element) => {
        if (!cancelled) {
          setTargetElement(element);
        }
      });

      return () => {
        cancelled = true;
        observer?.disconnect();
      };
    }

    // Watch for targetElement's removal from the DOM or when it no longer matches the selector
    const observer = new MutationObserver(() => {
      if (!document.body.contains(targetElement)) {
        setTargetElement(null);
        return;
      }

      // Check if the element still matches the selector (e.g., aria-hidden may have changed)
      const matchingElement = document.querySelector(selector);
      if (matchingElement !== targetElement) {
        setTargetElement(null);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });

    return () => {
      observer.disconnect();
    };
  }, [targetElement, selector]);

  return targetElement;
}
